import { FC, useMemo } from 'react';
import { Button } from '@mui/material';
import { useFormatAvailDisplayLabel } from '@/components/Focus/hooks/useFormatAvailDisplayLabel';
import { ScheduleAvailType } from '@/redux/slices/slice_types';
import AvailListContainer, { StyledChildren, StyledScroll } from './AvailListContainer';
import NewScheduleAvailItem from './NewScheduleAvailItem';
import { territoryGrouping } from '../../../constants';
import { groupTerritoriesByRegion } from '@/components/Focus/utils/newScheduleAvails';
import TerritoryGroupedAvailList from './TerritoryGroupAvailList';

type RemoveAvailListProps = {
  onItemRemove?: (right: string) => void;
  onItemsRemove?: (rights: string[]) => void;
  rightsList: string[];
  rightsOptions?: string[];
  type: ScheduleAvailType;
};

const RemoveAvailList: FC<RemoveAvailListProps> = ({ onItemRemove, onItemsRemove, rightsList, rightsOptions, type }) => {
  const isTerritoriesWithGroups = type === 'territories';

  const formatDisplayLabel = useFormatAvailDisplayLabel();
  const formatDisplayLabelForType = useMemo(() => (code: string) => formatDisplayLabel(code, type), [formatDisplayLabel, type]);

  const groupedSelectedTerritories = useMemo(() => {
    if (!isTerritoriesWithGroups) return [];
    return groupTerritoriesByRegion(rightsList, territoryGrouping);
  }, [isTerritoriesWithGroups, rightsList]);

  const handleRemoveMultipleRights = (rightsToRemove: string[]) => {
    if (!rightsToRemove.length) return;

    if (onItemsRemove) {
      onItemsRemove(rightsToRemove);
      return;
    }

    rightsToRemove.forEach((right) => onItemRemove?.(right));
  };

  const handleClearAll = () => {
    handleRemoveMultipleRights(rightsList);
  };

  return (
    <AvailListContainer
      heading={rightsList.length ? `Selected (${rightsList.length})` : 'Selected'}
      headerRight={
        <Button disabled={!rightsList.length || (!onItemRemove && !onItemsRemove)} onClick={handleClearAll} variant="text">
          Clear all
        </Button>
      }
    >
      <StyledScroll data-type={type}>
        {isTerritoriesWithGroups ? (
          <StyledChildren>
            <TerritoryGroupedAvailList
              groups={groupedSelectedTerritories}
              formatDisplayLabel={formatDisplayLabelForType}
              hasRights={(code) => !!rightsOptions?.includes(code)}
              isSelected={() => false}
              onItemClick={(code) => onItemRemove?.(code)}
              onItemsClick={handleRemoveMultipleRights}
              variant="removable"
            />
          </StyledChildren>
        ) : (
          <StyledChildren>
            {rightsList.map((right) => (
              <NewScheduleAvailItem
                key={right}
                onClick={() => onItemRemove?.(right)}
                primaryLabel={formatDisplayLabelForType(right)}
                type="success"
                variant="removable"
                isSelectedItem={false}
                hasRights={!!rightsOptions?.includes(right)}
              />
            ))}
          </StyledChildren>
        )}
      </StyledScroll>
    </AvailListContainer>
  );
};

export default RemoveAvailList;
